import { gql } from "@apollo/client";

/**
 * AM-5716 follow-up — a narrow, host-owned mirror of @astrana/ui's
 * PATIENT_PROFILE_CONNECT_QUERY (astrana-ui-modules
 * src/graphql/queries/memberProfile.ts), trimmed to the one field this host
 * needs outside the module's own render tree: the resolved business member
 * id (`connectPatient.idsV2[].healthPlanMemberId`, PP's RmiMembId).
 *
 * Used ONLY on a hard-refresh PP profile (no `location.state`): the URL only
 * ever carries opaque GUIDs (`membMpiNo` + `membKeyId`), never the real
 * RmiMembId, so Create-Auth (`stableResolvePpMemberId`) and the
 * Duplicate-auth hint (`handoffMemberIdHint`) have nothing to read until this
 * resolves. This is NOT a search — it's the exact same point lookup
 * @astrana/ui's usePatientProfile already performs internally to resolve the
 * header off `(companyId && mkey)`; the host just needs its own copy of the
 * result because that hook's output isn't exposed outside <MemberProfile>.
 */
export const CONNECT_PATIENT_IDS = gql`
  query ConnectPatientIds($memberKeyId: String, $ipaId: String) {
    connectPatient(memberKeyId: $memberKeyId, ipaId: $ipaId) {
      idsV2 {
        ipaId
        healthPlanMemberId
      }
    }
  }
`;

export interface ConnectPatientIdsVars {
  memberKeyId?: string;
  ipaId?: string;
}

export interface ConnectPatientIdsTuple {
  ipaId?: string | null;
  healthPlanMemberId?: string | null;
}

export interface ConnectPatientIdsResponse {
  connectPatient: {
    idsV2?: ConnectPatientIdsTuple[] | null;
  } | null;
}

/**
 * AM-5868: `idsV2` replaces the deprecated single `ids` object with a LIST of id
 * tuples — one per source system the member is enrolled under. `ids` collapsed
 * those to a single tuple and picked the wrong one for multi-enrollment members,
 * so this lookup could hand Create-Auth a `membId` belonging to a DIFFERENT
 * enrollment. Both call sites already scope the query by `ipaId`, so select the
 * tuple for the IPA we actually asked about; fall back to the first tuple, which
 * is the whole list whenever the member has just one.
 *
 * Mirrors `selectPatientIds` in @astrana/ui's usePatientProfile — keep the two in
 * step, this file is a deliberate host-owned mirror of that module's query.
 */
export const selectHealthPlanMemberId = (
  data: ConnectPatientIdsResponse | null | undefined,
  requestedIpaId?: string | null,
): string | undefined => {
  const tuples = data?.connectPatient?.idsV2;
  if (!tuples?.length) return undefined;
  const forRequestedIpa = requestedIpaId
    ? tuples.find((tuple) => tuple.ipaId === requestedIpaId)
    : undefined;
  return (forRequestedIpa ?? tuples[0]).healthPlanMemberId ?? undefined;
};
